import React from 'react';
import useMediaQuery from '../hooks/useMediaQuery';
import { useLanguage } from '../contexts/LanguageContext';

// Gold gradient matching the flyer: from-yellow-400 to-yellow-600
const GOLD_GRADIENT = 'linear-gradient(135deg, #F59E0B 0%, #D97706 60%, #B45309 100%)';
const GOLD_LIGHT = 'linear-gradient(135deg, #FEF9C3 0%, #FEF3C7 100%)';

const CheckIcon = ({ color = '#D97706' }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
        <path d="M20 6L9 17l-5-5" />
    </svg>
);

const StarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
    </svg>
);

const GoldMembership = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { t } = useLanguage();

    const paidFeatures = t('goldMembership.paidFeatures');
    const freeFeatures = t('goldMembership.freeFeatures');

    const styles = {
        section: {
            width: '100%',
            padding: isMobile ? '3.5rem 1rem' : '5rem 2rem',
            background: '#FAFAFA',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        eyebrow: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: GOLD_GRADIENT,
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: '700',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '5px 14px',
            borderRadius: '999px',
            marginBottom: '1.25rem',
        },
        headline: {
            fontSize: isMobile ? '1.75rem' : '2.25rem',
            fontWeight: '800',
            color: '#1A202C',
            textAlign: 'center',
            marginBottom: '0.75rem',
            lineHeight: '1.2',
            fontFamily: "'Montserrat', sans-serif",
        },
        subheadline: {
            fontSize: '1rem',
            color: '#4A5568',
            textAlign: 'center',
            maxWidth: '560px',
            lineHeight: '1.7',
            marginBottom: '3rem',
        },
        card: {
            width: '100%',
            maxWidth: '960px',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
        },
        // Top gold header of the card
        cardHeader: {
            background: GOLD_GRADIENT,
            padding: isMobile ? '2rem 1.5rem' : '2.5rem 3rem',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '1.5rem',
        },
        cardHeaderLeft: {},
        planName: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#fff',
            fontSize: '1.5rem',
            fontWeight: '800',
            fontFamily: "'Montserrat', sans-serif",
            marginBottom: '0.35rem',
        },
        planTagline: {
            color: 'rgba(255,255,255,0.85)',
            fontSize: '0.9rem',
            fontWeight: '500',
        },
        priceBadge: {
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(8px)',
            borderRadius: '16px',
            padding: '1rem 1.5rem',
            textAlign: isMobile ? 'left' : 'center',
            border: '1px solid rgba(255,255,255,0.3)',
            flexShrink: 0,
        },
        priceAmount: {
            color: '#fff',
            fontSize: '2.25rem',
            fontWeight: '900',
            fontFamily: "'Montserrat', sans-serif",
            lineHeight: '1',
        },
        priceYear: {
            color: 'rgba(255,255,255,0.8)',
            fontSize: '0.8rem',
            fontWeight: '500',
            marginTop: '4px',
        },
        priceMonthly: {
            color: '#FEF9C3',
            fontSize: '0.75rem',
            fontWeight: '600',
            marginTop: '2px',
        },
        // Body of card — two columns
        cardBody: {
            background: '#fff',
            padding: isMobile ? '2rem 1.5rem' : '2.5rem 3rem',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '2.5rem' : '3rem',
        },
        columnTitle: {
            fontSize: '0.7rem',
            fontWeight: '800',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '1.25rem',
            paddingBottom: '0.75rem',
            borderBottom: '2px solid',
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem',
        },
        featureItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            fontSize: '0.9rem',
            color: '#2D3748',
            lineHeight: '1.5',
        },
        // Footer CTA strip
        cardFooter: {
            background: '#FFFBEB',
            borderTop: '1px solid #FDE68A',
            padding: isMobile ? '1.25rem 1.5rem' : '1.25rem 3rem',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
        },
        footerText: {
            fontSize: '0.85rem',
            color: '#92400E',
            fontWeight: '500',
        },
        footerLink: {
            fontSize: '0.85rem',
            color: '#B45309',
            fontWeight: '700',
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    };

    return (
        <section style={styles.section}>
            {/* Eyebrow */}
            <div style={styles.eyebrow}>
                <StarIcon />
                {t('goldMembership.eyebrow')}
            </div>

            {/* Headline */}
            <h2 style={styles.headline}>{t('goldMembership.headline')}</h2>
            <p style={styles.subheadline}>{t('goldMembership.subheadline')}</p>

            {/* Card */}
            <div style={styles.card}>

                {/* Gold Header */}
                <div style={styles.cardHeader}>
                    <div style={styles.cardHeaderLeft}>
                        <div style={styles.planName}>
                            <StarIcon />
                            {t('goldMembership.planName')}
                        </div>
                        <p style={styles.planTagline}>{t('goldMembership.planTagline')}</p>
                    </div>
                    <div style={styles.priceBadge}>
                        <div style={styles.priceAmount}>€60</div>
                        <div style={styles.priceYear}>{t('goldMembership.perYear')}</div>
                        <div style={styles.priceMonthly}>{t('goldMembership.perMonth')}</div>
                    </div>
                </div>

                {/* Two-column feature body */}
                <div style={styles.cardBody}>

                    {/* Paid features — Gold */}
                    <div>
                        <div style={{
                            ...styles.columnTitle,
                            color: '#B45309',
                            borderColor: '#FCD34D',
                        }}>
                            ⭐ {t('goldMembership.goldFeaturesTitle')}
                        </div>
                        <ul style={styles.featureList}>
                            {paidFeatures.map((feature, i) => (
                                <li key={i} style={styles.featureItem}>
                                    <CheckIcon color="#D97706" />
                                    <span><strong>{feature.title}</strong>{feature.desc ? ` — ${feature.desc}` : ''}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Free features — Grey */}
                    <div>
                        <div style={{
                            ...styles.columnTitle,
                            color: '#4A5568',
                            borderColor: '#E2E8F0',
                        }}>
                            ✓ {t('goldMembership.freeFeaturesTitle')}
                        </div>
                        <ul style={styles.featureList}>
                            {freeFeatures.map((feature, i) => (
                                <li key={i} style={{ ...styles.featureItem, color: '#4A5568' }}>
                                    <CheckIcon color="#68D391" />
                                    <span><strong>{feature.title}</strong>{feature.desc ? ` — ${feature.desc}` : ''}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                </div>

                {/* Footer strip */}
                <div style={styles.cardFooter}>
                    <p style={styles.footerText}>{t('goldMembership.footerText')}</p>
                </div>
            </div>
        </section>
    );
};

export default GoldMembership;
